import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_ENDPOINTS } from '../config/api-config';
import axios from '../utils/axios-config';
import { useCommunityStore } from '../stores';

/**
 * Communities Hook
 * Handles all community-related operations (MongoDB stored, not DKG minted)
 * - Communities have OIDs generated on backend
 * - Users can create, join, and leave communities
 * - Discussions are linked to communities via communityOid
 * - Integrates with Zustand for selected community state
 */
export function useCommunities() {
  const queryClient = useQueryClient();
  const { setSelectedCommunity, addRecentCommunity } = useCommunityStore();

  // Get all communities (public endpoint - no auth required)
  const useGetAllCommunities = (filters = {}) => {
    return useQuery({
      queryKey: ['communities', 'all', filters],
      queryFn: async () => {
        try {
          const response = await axios.get(API_ENDPOINTS.COMMUNITIES.GET_ALL, {
            params: filters,
          });

          const communities = response.data?.communities || response.data || [];
          let filtered = Array.isArray(communities) ? communities : [];

          // Client-side category filter
          if (filters.category && filters.category !== 'All') {
            filtered = filtered.filter(
              (c) =>
                c.category?.toLowerCase() === filters.category.toLowerCase()
            );
          }

          // Client-side search filter
          if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            filtered = filtered.filter(
              (c) =>
                c.name?.toLowerCase().includes(searchLower) ||
                c.description?.toLowerCase().includes(searchLower)
            );
          }

          return filtered;
        } catch (error) {
          // Return empty array on error (don't throw for public pages)
          console.error('Error fetching communities:', error);
          return [];
        }
      },
      staleTime: 1000 * 60 * 5,
      retry: 1,
    });
  };

  // Get community by OID
  const useGetCommunityByOid = (oid) => {
    return useQuery({
      queryKey: ['communities', 'oid', oid],
      queryFn: async () => {
        const response = await axios.get(
          API_ENDPOINTS.COMMUNITIES.GET_BY_OID(oid)
        );
        const community = response.data?.community || response.data || null;

        // Add to recent communities if found
        if (community) {
          addRecentCommunity(community);
        }

        return community;
      },
      enabled: !!oid,
      staleTime: 1000 * 60 * 5,
      retry: false, // Don't retry on 404 - community doesn't exist
    });
  };

  // Get community by slug
  const useGetCommunityBySlug = (slug) => {
    return useQuery({
      queryKey: ['communities', 'slug', slug],
      queryFn: async () => {
        const response = await axios.get(
          API_ENDPOINTS.COMMUNITIES.GET_BY_SLUG(slug)
        );
        const community = response.data?.community || response.data || null;

        // Add to recent communities if found
        if (community) {
          addRecentCommunity(community);
        }

        return community;
      },
      enabled: !!slug,
      staleTime: 1000 * 60 * 5,
      retry: false, // Don't retry on 404 - community doesn't exist
    });
  };

  // Get user's joined communities (requires auth)
  const useGetUserCommunities = (userOid) => {
    return useQuery({
      queryKey: ['communities', 'user', userOid],
      queryFn: async () => {
        const response = await axios.get(
          API_ENDPOINTS.COMMUNITIES.GET_USER_COMMUNITIES(userOid)
        );
        return response.data?.communities || response.data || [];
      },
      enabled: !!userOid,
      staleTime: 1000 * 60 * 2,
    });
  };

  // Check if user is a member of a community
  const useCheckMembership = (communityOid, userOid) => {
    return useQuery({
      queryKey: ['communities', communityOid, 'membership', userOid],
      queryFn: async () => {
        const response = await axios.get(
          API_ENDPOINTS.COMMUNITIES.CHECK_MEMBERSHIP(communityOid, userOid)
        );
        return response.data;
      },
      enabled: !!communityOid && !!userOid,
      staleTime: 1000 * 60 * 2,
      retry: false, // Don't retry on 404 - community doesn't exist
    });
  };

  // Get community discussions
  const useGetCommunityDiscussions = (communityOid, filters = {}) => {
    return useQuery({
      queryKey: ['communities', communityOid, 'discussions', filters],
      queryFn: async () => {
        const response = await axios.get(
          API_ENDPOINTS.COMMUNITIES.GET_DISCUSSIONS(communityOid),
          { params: filters }
        );
        return response.data?.discussions || response.data || [];
      },
      enabled: !!communityOid,
      staleTime: 1000 * 60 * 2,
    });
  };

  // Create community (requires auth)
  const useCreateCommunity = () => {
    return useMutation({
      mutationFn: async (communityData) => {
        const response = await axios.post(
          API_ENDPOINTS.COMMUNITIES.CREATE,
          communityData
        );
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['communities'] });
      },
    });
  };

  // Update community (requires auth + ownership)
  const useUpdateCommunity = () => {
    return useMutation({
      mutationFn: async ({ communityOid, data }) => {
        const response = await axios.put(
          API_ENDPOINTS.COMMUNITIES.UPDATE(communityOid),
          data
        );
        return response.data;
      },
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ['communities'] });
        queryClient.invalidateQueries({
          queryKey: ['communities', 'oid', variables.communityOid],
        });
      },
    });
  };

  // Delete community (requires auth + ownership)
  const useDeleteCommunity = () => {
    return useMutation({
      mutationFn: async (communityOid) => {
        const response = await axios.delete(
          API_ENDPOINTS.COMMUNITIES.DELETE(communityOid)
        );
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['communities'] });
      },
    });
  };

  // Join community (requires auth)
  const useJoinCommunity = () => {
    return useMutation({
      mutationFn: async ({ communityOid, creator }) => {
        if (!creator) {
          throw new Error('Wallet address is required to join a community');
        }
        const response = await axios.post(
          API_ENDPOINTS.COMMUNITIES.JOIN(communityOid),
          { creator }
        );
        return response.data;
      },
      onSuccess: (_, { communityOid }) => {
        queryClient.invalidateQueries({ queryKey: ['communities'] });
        queryClient.invalidateQueries({
          queryKey: ['communities', 'oid', communityOid],
        });
        queryClient.invalidateQueries({ queryKey: ['communities', 'user'] });
      },
    });
  };

  // Leave community (requires auth)
  const useLeaveCommunity = () => {
    return useMutation({
      mutationFn: async ({ communityOid, creator }) => {
        if (!creator) {
          throw new Error('Wallet address is required to leave a community');
        }
        const response = await axios.post(
          API_ENDPOINTS.COMMUNITIES.LEAVE(communityOid),
          { creator }
        );
        return response.data;
      },
      onSuccess: (_, { communityOid }) => {
        queryClient.invalidateQueries({ queryKey: ['communities'] });
        queryClient.invalidateQueries({
          queryKey: ['communities', 'oid', communityOid],
        });
        queryClient.invalidateQueries({ queryKey: ['communities', 'user'] });
      },
    });
  };

  // Get community members
  const useGetCommunityMembers = (communityOid) => {
    return useQuery({
      queryKey: ['communities', communityOid, 'members'],
      queryFn: async () => {
        const response = await axios.get(
          API_ENDPOINTS.COMMUNITIES.GET_MEMBERS(communityOid)
        );
        return response.data?.members || response.data || [];
      },
      enabled: !!communityOid,
      staleTime: 1000 * 60 * 5,
    });
  };

  // Get community staff (owner, admins, moderators)
  const useGetCommunityStaff = (communityOid) => {
    return useQuery({
      queryKey: ['communities', communityOid, 'staff'],
      queryFn: async () => {
        const response = await axios.get(
          API_ENDPOINTS.COMMUNITIES.GET_STAFF(communityOid)
        );
        return response.data?.staff || [];
      },
      enabled: !!communityOid,
      staleTime: 1000 * 60 * 5,
    });
  };

  // Get community stats
  const useGetCommunityStats = (communityOid) => {
    return useQuery({
      queryKey: ['communities', communityOid, 'stats'],
      queryFn: async () => {
        const response = await axios.get(
          API_ENDPOINTS.COMMUNITIES.GET_STATS(communityOid)
        );
        return response.data?.stats || response.data || null;
      },
      enabled: !!communityOid,
      staleTime: 1000 * 60 * 2,
    });
  };

  return {
    useGetAllCommunities,
    useGetCommunityByOid,
    useGetCommunityBySlug,
    useGetUserCommunities,
    useCheckMembership,
    useGetCommunityDiscussions,
    useCreateCommunity,
    useUpdateCommunity,
    useDeleteCommunity,
    useJoinCommunity,
    useLeaveCommunity,
    useGetCommunityMembers,
    useGetCommunityStaff,
    useGetCommunityStats,
  };
}

export default useCommunities;
